import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { cssMap } from '@compiled/react';
import { graphql } from 'react-relay';

import Heading from '@atlaskit/heading';
import { Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { usePublish } from '@atlaskit/rovo-triggers';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { fg } from '@confluence/feature-gating';

import type {
	PopularQuestionsQuery$data,
	PopularQuestionsQuery$variables,
} from './__generated__/PopularQuestionsQuery.graphql.ts';
import { QuestionItem } from './QuestionItem.tsx';
import { MOCK_DATA } from './mockData.ts';

type FAQEntry = NonNullable<
	NonNullable<
		NonNullable<PopularQuestionsQuery$data['confluence_smartSpaceOverview']>['faq']
	>['entries']
>[number];

export type Source = FAQEntry['sources'][number];

const PopularQuestionsQuery = graphql`
	query PopularQuestionsQuery($cloudId: ID!, $spaceKey: String!) {
		confluence_smartSpaceOverview(spaceKey: $spaceKey, cloudId: $cloudId) {
			faq {
				entries {
					question
					answer
					followUpQuestions
					sources {
						content {
							id
							title
							excerpt
							embeddedProduct
							links {
								webui
							}
						}
					}
				}
			}
		}
	}
`;

type PopularQuestionsProps = {
	spaceKey: string;
};

export const PopularQuestions = ({ spaceKey }: PopularQuestionsProps) => {
	const { formatMessage } = useIntl();
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const publish = usePublish('ai-mate');
	const { cloudId } = useSessionData();

	const { data: queryData } = useQueryInterop<
		PopularQuestionsQuery$data,
		PopularQuestionsQuery$variables
	>(PopularQuestionsQuery, {
		variables: {
			cloudId,
			spaceKey,
		},
		skip: !cloudId,
	});

	const data = fg('smart-space-overview-faq-use-backend-query') ? queryData : MOCK_DATA;

	const handleToggle = useCallback((id: string) => {
		setExpandedId((prev) => (prev === id ? null : id));
	}, []);

	const formatSourcesContext = useCallback(
		(sources: readonly Source[]) => {
			if (sources.length === 0) {
				return '';
			}
			const sourceLines = sources.map(
				(source) =>
					`- [${source?.content?.title}](${window.location.origin}${source?.content?.links?.webui})`,
			);
			return `\n\n${formatMessage(i18n.sourcesLabel)}\n${sourceLines.join('\n')}`;
		},
		[formatMessage],
	);

	const createSpaceContext = useCallback(
		(parentQuestion: FAQEntry) => {
			const spaceOverviewUrl = `${window.location.origin}${SPACE_OVERVIEW.toUrl({ spaceKey })}`;

			const humanMessageContent = formatMessage(i18n.humanMessagePrompt, {
				link: spaceOverviewUrl,
				question: parentQuestion.question,
			});
			const agentMessageContent = `${parentQuestion.answer}${formatSourcesContext(parentQuestion.sources)}`;

			return {
				human_message: {
					content: humanMessageContent,
				},
				agent_message: {
					content: agentMessageContent,
				},
			};
		},
		[spaceKey, formatSourcesContext, formatMessage],
	);

	const publishRovoChat = useCallback(
		(questionText: string, parentQuestion: FAQEntry) => {
			const context = createSpaceContext(parentQuestion);

			publish({
				type: 'chat-new',
				source: 'space-overview-popular-questions',
				data: {
					name: questionText.substring(0, 30),
					dialogues: [context],
					prompt: questionText,
				},
			});
		},
		[publish, createSpaceContext],
	);

	const handleFollowUp = useCallback(
		(followUpQuestion: string, parentQuestion: FAQEntry) =>
			publishRovoChat(followUpQuestion, parentQuestion),
		[publishRovoChat],
	);

	const handleAskRovo = useCallback(
		(customQuestion: string, parentQuestion: FAQEntry) =>
			publishRovoChat(customQuestion, parentQuestion),
		[publishRovoChat],
	);

	if ((data?.confluence_smartSpaceOverview?.faq?.entries?.length ?? 0) > 0) {
		return (
			<section css={styles.container} aria-label={formatMessage(i18n.heading)}>
				<Stack space="space.150">
					<Heading size="medium">{formatMessage(i18n.heading)}</Heading>
					<Stack space="space.150">
						{data?.confluence_smartSpaceOverview?.faq?.entries?.map((question) => {
							const questionKey = question.question;
							return (
								<QuestionItem
									key={questionKey}
									question={question}
									isExpanded={expandedId === questionKey}
									onToggle={handleToggle}
									onFollowUp={handleFollowUp}
									onAskRovo={handleAskRovo}
								/>
							);
						})}
					</Stack>
				</Stack>
			</section>
		);
	}

	return null;
};

const i18n = defineMessages({
	heading: {
		id: 'space-overview.smart-space-overview.popular-questions.heading',
		defaultMessage: 'Popular questions',
		description: 'Heading for the popular questions section on smart space overview.',
	},
	humanMessagePrompt: {
		id: 'space-overview.smart-space-overview.popular-questions.human-message-prompt',
		defaultMessage: 'Question about space {link}: {question}',
		description: 'The initial human prompt sent to the chat when a popular question is selected.',
	},
	sourcesLabel: {
		id: 'space-overview.smart-space-overview.popular-questions.sources-label',
		defaultMessage: 'Sources:',
		description: 'Label for the sources section in the agent response.',
	},
});

const styles = cssMap({
	container: {
		paddingBlock: token('space.300'),
		maxWidth: '830px',
	},
});
